/**
 * Sentry frontend bootstrap.
 *
 * Fetches the DSN + environment + trace sample rate from the backend
 * (`GET /api/config/sentry`) so the DSN is not baked into the CRA bundle.
 * If no DSN is configured, we return a resolved promise and the SDK stays
 * inert — the app boots normally without error monitoring.
 *
 * Called once at boot from `src/index.js` BEFORE React root creation, so
 * the browserTracingIntegration + reactRouterV6BrowserTracingIntegration
 * hook into the initial pageload transaction.
 */
import React from "react";
import * as Sentry from "@sentry/react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

let initialized = false;

export async function initSentry() {
  if (initialized) return;
  try {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const res = await fetch(`${backend}/api/config/sentry`, { credentials: "omit" });
    if (!res.ok) return;
    const cfg = await res.json();
    if (!cfg?.dsn) return;

    Sentry.init({
      dsn: cfg.dsn,
      environment: cfg.environment || "production",
      release: cfg.release || "hbf-frontend",
      tracesSampleRate: typeof cfg.traces_sample_rate === "number" ? cfg.traces_sample_rate : 0.1,
      // Capture Replays only on errors to control cost.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      // Denylist noisy 3rd-party errors that aren't actionable.
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "Non-Error promise rejection captured",
      ],
      beforeSend(event) {
        // Drop events sourced from browser extensions (e.g. adblockers,
        // password managers) — they aren't actionable for us.
        const first = event?.exception?.values?.[0];
        const frames = first?.stacktrace?.frames || [];
        if (frames.some(f => (f?.filename || "").startsWith("chrome-extension://") ||
                             (f?.filename || "").startsWith("moz-extension://"))) {
          return null;
        }
        return event;
      },
    });

    initialized = true;
    console.info("[Sentry] initialised", { env: cfg.environment, tsr: cfg.traces_sample_rate });
  } catch (e) {
    console.warn("[Sentry] init skipped:", e?.message || e);
  }
}

export { Sentry };
